import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { DobleAutenticacionService } from 'src/app/services/doble-autenticacion.service';
import { ParametrosService } from 'src/app/services/parametros.service';
import { NeoTipoDocIdentidad } from '../../../../comun/models/neoTipoDocIdentidad';
import { NeoTipoDocIdentidadService } from '../../../../comun/services/neoTipoDocIdentidad.service';
import { NeoIntermediarioFinancieroService } from '../../../Service/neoIntermediarioFinanciero.service';
import { UserService } from '../../../../services/user.service';
import { SweetAlertService } from 'src/app/services/sweet-alert.service';
import { BancoldexService } from 'src/app/comun/services/bancoldex.service';
import { ReCaptchaV3Service } from 'ng-recaptcha-2';
import { setAuthFlowOrigen } from 'src/app/core/auth-flow-origen.storage';
import {
  getDocumentPatternMessage,
  numeroDocumentoPorTipoDocValidator,
  TipoDocIdentidadParaValidacion,
} from 'src/app/shared/utils/form-validators.util';
import { PASSWORD_COMPLEXITY_PATTERN } from 'src/app/shared/utils/validation.constants';
import { AnalyticsTagService } from 'src/app/services/analytics-tag.service';

const mensajeBloqueado = 'PAR-110';

@Component({
  selector: 'app-login-if',
  templateUrl: './login-if.component.html',
  styleUrls: ['./login-if.component.scss'],
  providers: [NeoTipoDocIdentidadService, NeoIntermediarioFinancieroService],
  standalone: false,
})
export class LoginIFComponent implements OnInit {
  loginForm: UntypedFormGroup;
  listaDocumentos: NeoTipoDocIdentidad[] = [];
  isVisible = false;
  validarContrasena = PASSWORD_COMPLEXITY_PATTERN;
  mensajeBloqueo = '';
  captchaToken: string | null = null;
  captchaFailCount = 0;
  isBlockedCaptcha = false;
  private captchaSubmitting = false;

  constructor(
    private readonly _user: UserService,
    private readonly router: Router,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly _parametro: ParametrosService,
    private readonly _dobleAutenticacion: DobleAutenticacionService,
    private readonly _neoTipoDocIdentidad: NeoTipoDocIdentidadService,
    private readonly _neoIntermediarioFinanciero: NeoIntermediarioFinancieroService,
    private readonly _sweetAlert: SweetAlertService,
    private readonly _bancoldexService: BancoldexService,
    private readonly recaptchaV3Service: ReCaptchaV3Service,
    private readonly analyticsTag: AnalyticsTagService
  ) {
    this.loginForm = this.formBuilder.group({
      idTipoDocIdentidad: ['', Validators.required],
      numeroDocumento: [
        '',
        [
          Validators.required,
          Validators.maxLength(15),
          numeroDocumentoPorTipoDocValidator(
            () => this.documentosParaValidacion,
            'idTipoDocIdentidad'
          ),
        ],
      ],
      password: [
        '',
        [
          Validators.required,
          Validators.minLength(8),
          Validators.pattern(this.validarContrasena),
        ],
      ],
    });
  }

  /** Tipado compatible con la directiva/validador del Home (Number → number). */
  get documentosParaValidacion(): TipoDocIdentidadParaValidacion[] {
    return this.listaDocumentos.map((d) => ({
      idTipoDocIdentidad: Number(d.idTipoDocIdentidad),
      codigo: d.codigo != null ? String(d.codigo) : undefined,
    }));
  }

  get mensajeErrorNumeroDocumento(): string {
    return getDocumentPatternMessage(this.loginForm.get('numeroDocumento'));
  }

  ngOnInit(): void {
    setAuthFlowOrigen('if');
    const blockTime = localStorage.getItem('captchaBlock');
    if (blockTime) {
      const currentTime = Date.now();
      const unblockTime = Number.parseInt(blockTime, 10);
      if (currentTime < unblockTime) {
        this.isBlockedCaptcha = true;
        const remainingTime = unblockTime - currentTime;
        setTimeout(() => {
          this.isBlockedCaptcha = false;
          this.captchaFailCount = 0;
          this.clearCaptchaToken();
          localStorage.removeItem('captchaBlock');
        }, remainingTime);
      } else {
        this.isBlockedCaptcha = false;
        localStorage.removeItem('captchaBlock');
      }
    }
    this.getMensajeBloqueo();
    this._neoTipoDocIdentidad.getTiposDocIdentidad().subscribe((response) => {
      this.listaDocumentos = response.filter(
        (documento: NeoTipoDocIdentidad) =>
          documento.idTipoDocIdentidad != 1 &&
          documento.idTipoDocIdentidad != 4 &&
          documento.idTipoDocIdentidad != 6
      );
      this.loginForm.get('numeroDocumento')?.updateValueAndValidity();
    });
  }

  login(): void {
    if (this.isBlockedCaptcha) {
      this._sweetAlert.errorModal(
        'Demasiados intentos',
        true,
        'Tu acceso ha sido temporalmente bloqueado, por seguridad, debido a múltiples intentos incorrectos. Inténtalo nuevamente en 2 minutos.'
      );
      return;
    }
    const body = this.loginForm.value;
    body.idTipoDocIdentidad = +this.loginForm.get('idTipoDocIdentidad')!.value;
    body.captchaToken = this.captchaToken;
    this._neoIntermediarioFinanciero.login(body).then(
      (data: any) => {
        this.captchaFailCount++;
        this.handleLoginResponse(data);
        if (this.captchaFailCount > 3) {
          this.blockCaptcha();
        } else {
          this.clearCaptchaToken();
        }
      },
      () => {
        this._sweetAlert.errorModal(
          '¡Error!',
          true,
          'La información ingresada es inválida, por favor intente nuevamente.'
        );
      }
    );
  }

  private handleLoginResponse(data: any): void {
    if (data.exitoso === true || data.exitoso === 'true') {
      this.handleLoginSuccess(data);
      return;
    }
    if (
      (data.exitoso === false || data.exitoso === 'false') &&
      data.codigoError &&
      data.codigoError !== '001' &&
      data.codigoError !== '002' &&
      data.codigoError !== '003'
    ) {
      this._sweetAlert.errorModal(
        '¡Error!',
        true,
        data.error ? data.error : data.mensajeError
      );
      return;
    }
    if ((data.exitoso === false || data.exitoso === 'false') && data.bloked) {
      this.handleBlockedUser(data);
      return;
    }
    this.handleInvalidCredentials(data);
  }

  private handleLoginSuccess(data: any): void {
    if (data.segundoFactorAut) {
      this._parametro.sendOtp(data.correoElectronico).subscribe((response: any) => {
        if (response.exitoso === 'true') {
          this.captchaFailCount = 0;
          this._dobleAutenticacion.saveParametros({
            email: data.correoElectronico,
            token: response.token,
            path: 'intermediario',
            data: data,
          });
          this.router.navigateByUrl('/doble-autenticacion');
        } else {
          this._sweetAlert.errorModal(
            '¡Error!',
            true,
            response.error ? response.error : response.mensajeError
          );
        }
      });
      return;
    }
    this.captchaFailCount = 0;
    try {
      sessionStorage.removeItem('login-if');
    } catch {
      // ignore
    }
    this._user.saveUser(data);
    this.router.navigateByUrl('/intermediario/home');
  }

  private handleBlockedUser(data: any): void {
    this._sweetAlert
      .errorModal(
        '¡Error!',
        true,
        data.error || data.mensajeError || this.mensajeBloqueo,
        'Desbloquear Aquí'
      )
      .then((result) => {
        if (result.isConfirmed) {
          this.router.navigateByUrl('/desbloquear');
          localStorage.removeItem('captchaBlock');
        }
      });
  }

  /** Mismo criterio que micro: mensaje genérico + contador local → CTA desbloqueo tras reintentos. */
  private handleInvalidCredentials(data: any): void {
    const storageKey = 'login-if';
    let bloquear: {
      idTipoDocIdentidad?: number;
      numeroDocumento?: string;
      contador?: number;
    } | null = null;
    try {
      const raw = sessionStorage.getItem(storageKey);
      bloquear = raw ? JSON.parse(raw) : null;
    } catch {
      bloquear = null;
    }

    const idTipoDocIdentidad = this.loginForm.get('idTipoDocIdentidad')?.value;
    const numeroDocumento = this.loginForm.get('numeroDocumento')?.value;
    const isChange =
      idTipoDocIdentidad == bloquear?.idTipoDocIdentidad &&
      numeroDocumento == bloquear?.numeroDocumento;
    const isBloked = (bloquear?.contador ?? 0) > 2 ? isChange : false;

    let errorMessage =
      data.error ||
      data.mensajeError ||
      'La información ingresada es inválida, por favor intente nuevamente.';
    if (isBloked) {
      errorMessage = this.mensajeBloqueo || errorMessage;
    }
    const confirmButton = isBloked ? 'Desbloquear Aquí' : 'Aceptar';

    this._sweetAlert
      .errorModal('¡Error!', true, errorMessage, confirmButton)
      .then((result) => {
        if (result.isConfirmed && isBloked) {
          this.router.navigateByUrl('/desbloquear');
          try {
            sessionStorage.removeItem(storageKey);
          } catch {
            // ignore
          }
          localStorage.removeItem('captchaBlock');
        }
      });

    let contador = 1;
    if (bloquear?.contador && isChange) {
      contador = bloquear.contador + 1;
    }
    try {
      sessionStorage.setItem(
        storageKey,
        JSON.stringify({
          idTipoDocIdentidad: +idTipoDocIdentidad,
          numeroDocumento,
          contador,
        })
      );
    } catch {
      // ignore
    }
  }

  showPassword(): void {
    this.isVisible = !this.isVisible;
  }

  validarSelect(): void {
    this.loginForm.get('numeroDocumento')?.setValue('');
    this.loginForm.get('numeroDocumento')?.updateValueAndValidity();
  }

  validateField(key: string): boolean {
    const control = this.loginForm.get(key);
    return !!(control && (control.dirty || control.touched) && control.invalid);
  }

  trackLinkCta(ctaLabel: string, ctaElementId: string): void {
    this.analyticsTag.trackCtaClick({
      ctaLabel,
      ctaSection: 'login-if',
      ctaComponent: 'login-if',
      ctaElementId,
      hu: '8337',
    });
  }

  validateForm(): void {
    if (!this.loginForm.valid) {
      this.loginForm.markAllAsTouched();
      return;
    }
    if (this.captchaSubmitting) {
      return;
    }
    this.analyticsTag.trackCtaClick({
      ctaLabel: 'Inicia sesión',
      ctaSection: 'login-if',
      ctaComponent: 'login-if',
      ctaElementId: 'btn-login-if-iniciar',
      hu: '8337',
    });
    this.captchaSubmitting = true;
    this.recaptchaV3Service.execute('login_if').subscribe({
      next: (token) => {
        this.captchaToken = token;
        this.captchaSubmitting = false;
        this.login();
      },
      error: () => {
        this.captchaSubmitting = false;
        this._sweetAlert.errorModal(
          '¡Error!',
          true,
          'No se pudo validar el captcha. Inténtalo de nuevo.'
        );
      },
    });
  }

  getMensajeBloqueo(): void {
    const json = {
      idParametro: mensajeBloqueado,
      nombre: null,
      isParFuncional: true,
      pagina: 1,
    };
    this._bancoldexService.getFilterParametro(json).subscribe(
      (response: { data?: Array<{ valorParametro?: string }> }) => {
        const respuesta = response?.data;
        this.mensajeBloqueo = respuesta?.[0]?.valorParametro ?? '';
      },
      (error) => {
        this._sweetAlert.errorModal('Error!', true, error.error);
      }
    );
  }

  clearCaptchaToken(): void {
    this.captchaToken = null;
  }

  blockCaptcha(): void {
    const block = Date.now() + 2 * 60 * 1000;
    localStorage.setItem('captchaBlock', block.toString());
    this.isBlockedCaptcha = true;
    setTimeout(() => {
      this.isBlockedCaptcha = false;
      this.captchaFailCount = 0;
      this.clearCaptchaToken();
      localStorage.removeItem('captchaBlock');
    }, 2 * 60 * 1000);
  }
}
